import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/navbar/navbar';
import Footer from '../components/footer/footer';
import './About.css';
import SEO from '../components/SEO';

const VALUES = [
  { title: "Partner Empowerment", desc: "We support our drivers and chefs with standard subscription-only models and 0% sales commission.", icon: "🙌" },
  { title: "Passenger Safety", desc: "Every ride and delivery is monitored in real-time with comprehensive premium insurance policies.", icon: "🛡️" },
  { title: "Upfront Clarity", desc: "No hidden surcharges. All estimated fares are generated instantly before booking requests.", icon: "💎" },
  { title: "Local Impact", desc: "Empowering local cooks, private chefs, and bike dispatchers with clean economic opportunities.", icon: "🌍" }
];

const STATS = [
  { value: "10K+", label: "Trips Completed" },
  { value: "500+", label: "Partner Drivers" },
  { value: "150+", label: "Artisan Chefs" },
  { value: "4.9★", label: "Customer Rating" }
];

function About() {
  const navigate = useNavigate();

  return (
    <div className="about-page-wrapper">
      <SEO 
        title="About Us - Our Story & Core Mission"
        description="Learn about 9tro Ride, our subscription-based models for drivers, package dispatch logistics, and artisanal food delivery services."
        url="https://www.9troride.com/about"
      />
      <Navbar showSubNavbar={false} />

      {/* Background Decorative Blur Blobs */}
      <div className="bg-blob blob-1" />
      <div className="bg-blob blob-2" />

      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-container">
          <span className="hero-tag">Who We Are</span>
          <h1>Empowering journeys, deliveries, and local kitchens</h1>
          <p>We are building a transparent marketplace connecting riders, local chefs, and dispatch couriers on fair, subscription-first terms.</p>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="mission-section">
        <div className="mission-container">
          <div className="mission-content-box">
            <span className="section-mini-label">Our Purpose</span>
            <h2>Fair terms for drivers. Fast commutes for riders.</h2>
            <p>Traditional platforms extract high commission fees from drivers and culinary vendors. 9tro is built differently. By introducing a flat subscription-only matching model, we ensure drivers keep 100% of their fares, resulting in cheaper rides for passengers and higher earnings for local partners.</p>
          </div>
          <div className="mission-stats-grid">
            {STATS.map((stat, idx) => (
              <div key={idx} className="stat-card">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CEO Message Section */}
      <section className="ceo-message-section">
        <div className="ceo-message-container">
          <div className="ceo-message-card">
            <span className="section-mini-label">Letter from the Founder</span>
            <h2>A Letter from the Founder & CEO of 9TRORIDE</h2>
            
            <div className="ceo-letter-content">
              <p>To our global community, our partners, and everyone who keeps our cities moving,</p>
              
              <p>Every great journey starts with a simple step, but in our modern cities, that step has become increasingly complex. We live in an era of incredible connectivity, where technology promised to make our lives easier, our work more rewarding, and our transitions more seamless. Yet, looking at the global landscape of urban mobility, I saw a widening gap between that promise and the reality on the ground.</p>
              
              <p>My name is <strong>Emmanuel Oladimeji Adeyemi</strong>, and I founded 9TRORIDE because I believe technology should be a bridge to prosperity, not a barrier to it.</p>
              
              <p>For too long, the heartbeat of our urban economies, the hardworking individuals behind the wheel, have been treated as mere data points in a cold, distant algorithm. Across the globe, I have watched a troubling pattern emerge: drivers spending their lives navigating our streets, only to see a massive portion of their hard-earned income disappear into high platform commissions and hidden fees. On the other side, riders deal with rising costs and frequent cancellations, which are symptoms of a system that simply isn’t fair to the person providing the service.</p>
              
              <p>I knew there had to be a more equitable way. I knew we could do better.</p>
              
              <p>9TRORIDE was born from a simple but powerful vision: What if the platform worked for the driver, instead of the driver working for the platform?</p>
              
              <p>We are not just building another app; we are architecting a community-first technology ecosystem designed to redefine urban transit. At 9TRORIDE, we are replacing high, unpredictable commissions with a transparent, flat subscription model.</p>
              
              <p>This simple shift changes the dynamic completely:</p>
              
              <ul className="manifesto-list">
                <li><strong>For Drivers:</strong> Your hard work belongs to you. In our ecosystem, when you work harder, you earn more and you don’t pay more. By moving to a flat subscription model, we empower drivers to run their own business with predictable costs and uncapped potential. We want to restore the financial dignity that comes with keeping 100% of your effort.</li>
                <li><strong>For Riders:</strong> A respected, fairly compensated driver is the foundation of a great ride. By removing driver financial stress, we eliminate the root cause of cancellations and unreliable service. When you choose 9TRORIDE, you support a system built on safety, reliability, and mutual respect.</li>
                <li><strong>For our Partners:</strong> We offer a secure, scalable matching platform that prioritizes human connection and fairness over high corporate percentages.</li>
              </ul>
              
              <p>Whether navigating local neighborhoods or expanding to new hubs, the need for fairness is universal. 9TRORIDE is our answer to that call, a brand built on transparency, integrity, and progress.</p>
              
              <p>We are starting a new chapter in global mobility, one where the road is open, the terms are clear, and the driver is finally back in the driver’s seat where they belong.</p>
              
              <p>Thank you for trusting us with your journey. We are not just moving people; we are moving the world toward a fairer future.</p>
              
              <p>Let’s move forward, together.</p>
            </div>
            
            <div className="ceo-signature-block">
              <div className="ceo-signature-line">Emmanuel Oladimeji Adeyemi</div>
              <div className="ceo-title">Founder & CEO, 9TRORIDE LIMITED</div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Highlights */}
      <section className="services-highlight-section">
        <div className="services-highlight-container">
          <div className="section-header-centered">
            <h2>Our Core Ecosystem</h2>
            <p>A unified suite of local mobility and logistics services.</p>
          </div>
          <div className="services-preview-grid">
            <div className="service-preview-card">
              <span className="srv-emoji">🚗</span>
              <h3>9tro Ride</h3>
              <p>Affordable cab booking and luxury sedan travels. Choose between Classic, Premium, and SUV tiers with no surge surprises.</p>
              <button onClick={() => navigate('/')} className="card-link-btn">Book a Ride →</button>
            </div>
            <div className="service-preview-card">
              <span className="srv-emoji">🍕</span>
              <h3>9tro Eat</h3>
              <p>Delicious restaurant cuisines and premium private chef menus delivered in insulated food bags in under 30 minutes.</p>
              <button onClick={() => navigate('/9troeat')} className="card-link-btn">Order Food →</button>
            </div>
            <div className="service-preview-card">
              <span className="srv-emoji">📦</span>
              <h3>9tro Delivery</h3>
              <p>Same-day package and letter dispatch services via eco bicycles, motorbikes, and heavy cargo trucks with real-time tracking.</p>
              <button onClick={() => navigate('/package-delivery')} className="card-link-btn">Request Courier →</button>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="values-section">
        <div className="values-container">
          <div className="section-header-centered">
            <h2>The Principles Guiding Us</h2>
            <p>Building trust and transparency into every transaction.</p>
          </div>
          <div className="values-grid">
            {VALUES.map((val, idx) => (
              <div key={idx} className="value-card">
                <span className="value-icon">{val.icon}</span>
                <h3>{val.title}</h3>
                <p>{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="about-cta">
        <div className="about-cta-container">
          <h2>Ready to experience 9tro Ride?</h2>
          <p>Join thousands of users riding, eating, and delivering with transparent, upfront fares.</p>
          <div className="cta-actions">
            <button className="cta-btn-primary" onClick={() => navigate('/')}>Get Started</button>
            <button className="cta-btn-secondary" onClick={() => navigate('/pricing')}>View Pricing Plans</button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default About;
